export class AsyncUtils {
    static delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private static sleep(ms: number): Promise<void> {
        return new Promise((res) => setTimeout(res, ms));
    }

    private static isFunction(v: unknown): v is (...args: any[]) => any {
        return typeof v === "function";
    }

    /**
     * Waits for window[methodName] to become a function, then calls it.
     * Returns true if the call succeeded, false if not found / timed out / call threw.
     */
    static async waitForWindowMethodAndCall<K extends keyof Window>(
        methodName: K,
        ...argsAndMaybeOptions: [
            ...(Window[K] extends (...a: infer A) => any ? A : never),
            options?: WaitCallOptions
        ]
    ): Promise<boolean> {
        const last = argsAndMaybeOptions[argsAndMaybeOptions.length - 1] as unknown;

        const hasOptions =
            last &&
            typeof last === "object" &&
            ("intervalMs" in (last as any) ||
                "maxAttempts" in (last as any) ||
                "timeoutMs" in (last as any) ||
                "debug" in (last as any));

        const options: WaitCallOptions = hasOptions ? (last as WaitCallOptions) : {};
        const args = (hasOptions
            ? argsAndMaybeOptions.slice(0, -1)
            : argsAndMaybeOptions) as unknown[];

        const intervalMs = options.intervalMs ?? 50;
        const maxAttempts = options.maxAttempts ?? 100;
        const deadline =
            typeof options.timeoutMs === "number" ? Date.now() + options.timeoutMs : null;

        let attempts = 0;

        while (true) {
            const candidate = (window as any)[methodName];

            if (this.isFunction(candidate)) {
                try {
                    candidate(...args);
                    return true;
                } catch (err) {
                    if (options.debug) {
                        console.warn(
                            `[AsyncUtils.waitForWindowMethodAndCall] "${String(methodName)}" threw:`,
                            err
                        );
                    }
                    return false;
                }
            }

            attempts++;

            if (deadline !== null) {
                if (Date.now() >= deadline) return false;
            } else {
                if (attempts >= maxAttempts) return false;
            }

            await this.sleep(intervalMs);
        }
    }
}


export type WaitCallOptions = {
    intervalMs?: number;   // default 50
    maxAttempts?: number;  // default 100 (ignored if timeoutMs provided)
    timeoutMs?: number;    // overrides maxAttempts
    debug?: boolean;
};